import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  BusinessNoticeDetailRequest,
  BusinessNoticeDetailResponse,
  BusinessNoticeTempDetailRequest,
  BusinessNoticeTempDetailResponse,
  BusinessNoticeReplySaveRequest,
  BusinessNoticeReplySaveResponse,
  BusinessNoticeReplyRequest,
  BusinessNoticeReplyResponse,
  BusinessNoticeHasNewReplyResponse,
} from "~/types/common";

// Business Notice Temp Detail API 함수
const getBusinessNoticeDetail = async (
  params: BusinessNoticeDetailRequest
): Promise<BusinessNoticeDetailResponse> => {
  try {
    const { data } = await privateHttp.get<BusinessNoticeDetailResponse>(
      `${API_ENDPOINTS.BUSINESS_NOTICE.NOTICE_DETAIL}?notiRecvSeq=${params.notiRecvSeq}`
    );
    return data;
  } catch (error) {
    console.error("Business Notice Temp Detail API 호출 에러:", error);
    throw error;
  }
};

// Business Notice Temp Detail React Query Hook
export const useBusinessNoticeDetail = (
  params: BusinessNoticeDetailRequest
) => {
  return useQuery({
    queryKey: ["business-notice-temp-detail", params],
    queryFn: () => getBusinessNoticeDetail(params),
    enabled: !!params.notiRecvSeq,
  });
};

// Business Notice Temp Detail API 함수
const getBusinessNoticeTempDetail = async (
  params: BusinessNoticeTempDetailRequest
): Promise<BusinessNoticeTempDetailResponse> => {
  try {
    const { data } = await privateHttp.get<BusinessNoticeTempDetailResponse>(
      `${API_ENDPOINTS.BUSINESS_NOTICE.TEMP_DETAIL}?noticeSeq=${params.noticeSeq}`
    );
    return data;
  } catch (error) {
    console.error("Business Notice Temp Detail API 호출 에러:", error);
    throw error;
  }
};

// Business Notice Temp Detail React Query Hook
export const useBusinessNoticeTempDetail = (
  params: BusinessNoticeTempDetailRequest
) => {
  return useQuery({
    queryKey: ["business-notice-temp-detail", params],
    queryFn: () => getBusinessNoticeTempDetail(params),
    enabled: !!params.noticeSeq,
    staleTime: 5 * 60 * 1000, // 5분
    gcTime: 10 * 60 * 1000, // 10분
    refetchOnMount: "always", // 화면 진입 시마다 최신 데이터 재조회
  });
};

// Business Notice Reply Save API 함수
const postBusinessNoticeReplySave = async (
  params: BusinessNoticeReplySaveRequest
): Promise<BusinessNoticeReplySaveResponse> => {
  try {
    const { data } = await privateHttp.post<BusinessNoticeReplySaveResponse>(
      API_ENDPOINTS.BUSINESS_NOTICE.REPLY_SAVE,
      params
    );
    return data;
  } catch (error) {
    console.error("Business Notice Reply Save API 호출 에러:", error);
    throw error;
  }
};

// Business Notice Reply Save React Query Hook
export const useBusinessNoticeReplySave = () => {
  return useMutation({
    mutationFn: postBusinessNoticeReplySave,
  });
};

// Business Notice Reply API 함수
const getBusinessNoticeReply = async (
  params: BusinessNoticeReplyRequest
): Promise<BusinessNoticeReplyResponse> => {
  try {
    const { data } = await privateHttp.get<BusinessNoticeReplyResponse>(
      `${API_ENDPOINTS.BUSINESS_NOTICE.REPLY}?notiRecvSeq=${params.notiRecvSeq}`
    );
    return data;
  } catch (error) {
    console.error("Business Notice Reply API 호출 에러:", error);
    throw error;
  }
};

// Business Notice Reply React Query Hook
export const useBusinessNoticeReply = (params: BusinessNoticeReplyRequest) => {
  return useQuery({
    queryKey: ["business-notice-reply", params],
    queryFn: () => getBusinessNoticeReply(params),
    enabled: !!params.notiRecvSeq,
  });
};

// Business Notice Has New Reply API 함수
const getBusinessNoticeHasNewReply =
  async (): Promise<BusinessNoticeHasNewReplyResponse> => {
    try {
      const { data } = await privateHttp.get<BusinessNoticeHasNewReplyResponse>(
        API_ENDPOINTS.BUSINESS_NOTICE.HAS_NEW_REPLY
      );
      return data;
    } catch (error) {
      console.error("Business Notice Has New Reply API 호출 에러:", error);
      throw error;
    }
  };

// Business Notice Has New Reply React Query Hook
export const useBusinessNoticeHasNewReply = () => {
  return useQuery({
    queryKey: ["business-notice-has-new-reply"],
    queryFn: getBusinessNoticeHasNewReply,
    staleTime: 0, // 즉시 stale 상태로 만들어 매번 호출
    gcTime: 0, // 캐시하지 않음
    refetchOnMount: true, // 컴포넌트 마운트 시 항상 refetch
    refetchOnWindowFocus: true, // 윈도우 포커스 시 refetch
  });
};
